import { ChevronLeft, ChevronRight } from 'lucide-react';

/**
 * Compact pagination control.
 * Renders nothing if total <= limit (single page).
 */
export default function Pagination({ page, pages, total, limit, onChange, testIdPrefix = 'pagination' }) {
  if (!pages || pages <= 1) {
    if (typeof total === 'number' && total > 0) {
      return (
        <p className="text-white/30 text-xs font-sans py-3" data-testid={`${testIdPrefix}-summary`}>
          Showing {total} of {total}
        </p>
      );
    }
    return null;
  }

  const start = (page - 1) * limit + 1;
  const end = Math.min(page * limit, total);

  // Build page numbers — show first, last, current ±1, with ellipsis
  const pageNumbers = [];
  const add = (n) => pageNumbers.push(n);
  add(1);
  if (page - 2 > 2) pageNumbers.push('…');
  for (let n = Math.max(2, page - 1); n <= Math.min(pages - 1, page + 1); n++) {
    if (!pageNumbers.includes(n)) add(n);
  }
  if (page + 2 < pages - 1) pageNumbers.push('…');
  if (pages > 1) add(pages);

  const goTo = (n) => {
    if (typeof n !== 'number' || n < 1 || n > pages || n === page) return;
    onChange(n);
  };

  return (
    <div className="flex items-center justify-between gap-3 py-3 flex-wrap" data-testid={testIdPrefix}>
      <p className="text-white/40 text-xs font-sans" data-testid={`${testIdPrefix}-summary`}>
        {start}–{end} of {total}
      </p>
      <div className="flex items-center gap-1">
        <button
          onClick={() => goTo(page - 1)}
          disabled={page === 1}
          data-testid={`${testIdPrefix}-prev`}
          className="p-1.5 border border-white/10 text-white/60 hover:border-white/30 hover:text-white disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
        >
          <ChevronLeft className="w-3.5 h-3.5" />
        </button>
        {pageNumbers.map((n, idx) => (
          <button
            key={`${n}-${idx}`}
            onClick={() => goTo(n)}
            disabled={n === '…' || n === page}
            data-testid={`${testIdPrefix}-page-${n}`}
            className={`min-w-[28px] px-2 py-1 text-[11px] font-sans border transition-colors ${
              n === page
                ? 'bg-[#D4AF37] text-black border-[#D4AF37]'
                : n === '…'
                  ? 'border-transparent text-white/30 cursor-default'
                  : 'border-white/10 text-white/60 hover:border-white/30 hover:text-white'
            }`}
          >
            {n}
          </button>
        ))}
        <button
          onClick={() => goTo(page + 1)}
          disabled={page === pages}
          data-testid={`${testIdPrefix}-next`}
          className="p-1.5 border border-white/10 text-white/60 hover:border-white/30 hover:text-white disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
        >
          <ChevronRight className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
}
